import "./privacy-policy.scss";
import PageTitle from '../../components/page-title';

const siteName = process.env.REACT_APP_SITE_NAME;
const siteUrl = process.env.REACT_APP_SITE_URL;

function PrivacyPolicy() {
  return (
    <div className="h-auto p-4 pb-32 space-y-6 max-w-4xl mx-auto">
      <PageTitle title='Privacy Policy' />
      
      <div className="privacy-policy-content space-y-6">
        <p className="text-sm text-gray-600 mb-6">
          <strong>Effective Date: 26/06/2025</strong>
        </p>
        
        <p className="text-base leading-relaxed">
          This Privacy Policy explains how Premier Parking Enforcement Ltd ("we", "us", or "our") 
          collects, processes, shares, and retains personal data. It applies to users of our websites 
          (www.premierparkingenforcementltd.com and www.payppl.co.uk), motorists and/or vehicle 
          keepers, clients, and their representatives (collectively referred to as "you" or "your").
        </p>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">1. Personal Data We Collect</h2>
          <p className="text-base leading-relaxed">
            We may collect personal data through the following means:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>CCTV, ANPR, body-worn and other operative cameras</li>
            <li>Enforcement software and mobile applications used by our operatives</li>
            <li>The DVLA</li>
            <li>Information provided by you via websites, phone, post, email, payment machines, registration points, or permit systems</li>
          </ul>
          <p className="text-base leading-relaxed mt-4">
            Types of personal data collected may include:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Full name</li>
            <li>Address</li>
            <li>Vehicle Registration Number (VRN)</li>
            <li>Telephone numbers</li>
            <li>Email addresses</li>
            <li>Payment information (e.g., credit/debit card details)</li>
            <li>Web browser cookies</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">2. Cookies and Website Tracking</h2>
          <p className="text-base leading-relaxed">
            Our websites use cookies to enhance user experience and for basic analytics. Cookies may be 
            placed on your device for record-keeping and tracking purposes. You can adjust your browser 
            settings to refuse or alert you to cookies.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">3. How We Use Your Personal Data</h2>
          <p className="text-base leading-relaxed">
            We use your data for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>To process and manage parking payments</li>
            <li>To operate permit systems and enforce parking regulations</li>
            <li>To issue and manage Parking Charge Notices (PCNs)</li>
            <li>To obtain vehicle keeper data from the DVLA</li>
            <li>To manage appeals processes, including those via the Independent Appeals Service (IAS)</li>
            <li>To pursue enforcement action including legal proceedings</li>
            <li>To conduct statistical and performance analysis of our services</li>
            <li>To cooperate with law enforcement or security organisations in the detection or prevention of crime</li>
          </ul>
          <p className="text-base leading-relaxed mt-4">
            We do not sell, rent, or trade your personal data to third parties.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">4. Lawful Basis for Processing</h2>
          <p className="text-base leading-relaxed">
            We process your personal data under the following legal bases:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Legitimate interest – for the effective delivery and enforcement of our services</li>
            <li>Performance of a contract – when managing your parking, permit, or payment-related interactions</li>
            <li>Legal obligation – when required by law or in pursuit of legal claims</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">5. Data Sharing and Third Parties</h2>
          <p className="text-base leading-relaxed">
            Personal data is only shared where necessary to deliver our services or as required by law. 
            Where we use agents or subcontractors, appropriate safeguards are in place to protect your 
            data. Examples include:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Payment processors</li>
            <li>Legal representatives</li>
            <li>IT and system service providers</li>
            <li>DVLA or law enforcement authorities</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">6. Data Security</h2>
          <p className="text-base leading-relaxed">
            We implement appropriate technical and organisational measures to protect your personal 
            data from unauthorised access, alteration, disclosure, or destruction. All employees and 
            third-party processors are required to maintain data confidentiality.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">7. Your Rights</h2>
          <p className="text-base leading-relaxed">
            Under data protection laws (including the UK GDPR and Data Protection Act 2018), you 
            have the right to:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Request access to your personal data</li>
            <li>Request correction or erasure of your data</li>
            <li>Object to or restrict our processing in certain circumstances</li>
            <li>Lodge a complaint with the Information Commissioner's Office (ICO)</li>
          </ul>
          <p className="text-base leading-relaxed mt-4">
            You can find more detailed information on your rights at{' '}
            <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
              www.ico.org.uk
            </a>.
          </p>
          <p className="text-base leading-relaxed">
            Please note that your rights may be limited where processing is necessary for the 
            establishment, exercise, or defence of legal claims (e.g. PCN enforcement).
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">8. External Websites</h2>
          <p className="text-base leading-relaxed">
            Our websites may contain links to external third-party sites (e.g. ICO, IPC). We are not 
            responsible for the content or privacy practices of those websites.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">9. Changes to This Policy</h2>
          <p className="text-base leading-relaxed">
            We may update this Privacy Policy from time to time. The most current version will always 
            be posted on our website. We recommend reviewing this page periodically.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">10. Contact Us</h2>
          <p className="text-base leading-relaxed">
            If you have any questions or concerns about this Privacy Policy or your personal data, you 
            can contact us at:
          </p>
          <div className="bg-gray-50 p-4 rounded-lg mt-4">
            <p className="text-base leading-relaxed">
              <strong>DPO – Walton Wilkins</strong><br />
              Premier Parking Enforcement Ltd<br />
              Office 3, Block 4, Mint Drive<br />
              Hockley, Birmingham, B18 6EA<br />
              Email: <a href="mailto:info@premierparkingenforcementltd.com" className="text-blue-600 hover:text-blue-800 underline">
                info@premierparkingenforcementltd.com
              </a>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
